import React, { useState, useMemo, FormEvent, useEffect } from 'react';
import { Col, Row, Form, Button } from 'react-bootstrap';
import { Select } from 'antd';
import { GoodsProps } from '../api/Goods';
import { GoodsMpLinksBlock } from './GoodsMpLinksBlock';

export type GoodsItemEditProps = {
  isNew: boolean;
  data: GoodsProps;
  brands: { name: string }[];
  productCategories: { id: number; name: string }[];
  availableTags: { id: number; name: string }[];
  onTagsCatalogChange?: () => void;
  onSave: (props: GoodsProps) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  onError?: (error: string) => void;
};

export const GoodsItemEdit = ({
  isNew,
  data,
  brands,
  productCategories,
  availableTags,
  onTagsCatalogChange,
  onSave,
  onCancel,
  onDelete,
  onError,
}: GoodsItemEditProps) => {
  const [error, setError] = useState<string>('');
  const [article, setArticle] = useState(data.article);
  const [name, setName] = useState(data.name);
  const [brand, setBrand] = useState(data.brand);
  const [category, setCategory] = useState(data.category);
  const [tags, setTags] = useState<{ id?: number; name: string }[]>(
    data.tags || []
  );
  const [showForDistribution, setShowForDistribution] = useState(
    data.product_remnants?.show_for_distribution || false
  );
  const [isCombo, setIsCombo] = useState(data.is_combo);
  const [comboComposition, setComboComposition] = useState(
    data.comboComposition?.map((el) => ({
      ...el,
      value: el.value.toString(),
    })) || []
  );
  const [showAlias, setShowAlias] = useState(
    !!data.wb_data?.alias ||
      !!data.ozon_data?.alias ||
      !!data.ya_data?.alias ||
      !!data.sber_data?.alias ||
      !!data.ali_data?.alias
  );
  const [aliasWb, setAliasWb] = useState(data.wb_data?.alias || '');
  const [aliasOzon, setAliasOzon] = useState(data.ozon_data?.alias || '');
  const [aliasYa, setAliasYa] = useState(data.ya_data?.alias || '');
  const [aliasSber, setAliasSber] = useState(data.sber_data?.alias || '');
  const [aliasAli, setAliasAli] = useState(data.ali_data?.alias || '');
  const [barcode, setBarcode] = useState(data.wb_data?.barcode || '');

  useEffect(() => {
    setArticle(data.article);
    setName(data.name);
    setBrand(data.brand);
    setCategory(data.category);
    setTags(data.tags || []);
    setShowForDistribution(
      data.product_remnants?.show_for_distribution || false
    );
    setIsCombo(data.is_combo);
    setComboComposition(
      data.comboComposition?.map((el) => ({
        ...el,
        value: el.value.toString(),
      })) || []
    );
    setAliasWb(data.wb_data?.alias || '');
    setAliasOzon(data.ozon_data?.alias || '');
    setAliasYa(data.ya_data?.alias || '');
    setAliasSber(data.sber_data?.alias || '');
    setAliasAli(data.ali_data?.alias || '');
    setBarcode(data.wb_data?.barcode || '');
  }, [data]);

  const validForm = useMemo(() => {
    return (
      !!name &&
      !!article &&
      !!brand &&
      comboComposition.every((el) => !!el.article && !isNaN(parseInt(el.value)))
    );
  }, [name, article, brand, comboComposition]);

  const categoryOptions = useMemo(
    () =>
      [...productCategories]
        .sort((a, b) =>
          a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' })
        )
        .map((el) => ({ value: el.id, label: el.name })),
    [productCategories]
  );

  const tagOptions = useMemo(() => {
    const byLower = new Map<string, { value: string; label: string }>();
    for (const t of availableTags) {
      byLower.set(t.name.toLowerCase(), { value: t.name, label: t.name });
    }
    for (const t of tags) {
      const key = t.name.toLowerCase();
      if (!byLower.has(key)) {
        byLower.set(key, { value: t.name, label: t.name });
      }
    }
    return [...byLower.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'ru', { sensitivity: 'base' })
    );
  }, [availableTags, tags]);

  const handleSubmit = async (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!validForm) return;

    setError('');
    try {
      await onSave({
        article,
        name,
        brand,
        category,
        tags: tags
          .map((t) => {
            const trimmed = t.name.trim();
            if (!trimmed) return null;
            const existing = availableTags.find(
              (a) => a.name.toLowerCase() === trimmed.toLowerCase()
            );
            if (existing) return { id: existing.id, name: existing.name };
            if (t.id) return { id: t.id, name: trimmed };
            return { name: trimmed };
          })
          .filter(Boolean) as { id?: number; name: string }[],
        product_remnants: { show_for_distribution: showForDistribution },
        is_combo: isCombo,
        comboComposition: isCombo
          ? comboComposition.map((el) => ({ ...el, value: parseInt(el.value) }))
          : undefined,
        wb_data: {
          alias: aliasWb ? aliasWb : null,
          barcode: barcode ? barcode : null,
        },
        ozon_data: { alias: aliasOzon ? aliasOzon : null },
        ya_data: { alias: aliasYa ? aliasYa : null },
        sber_data: { alias: aliasSber ? aliasSber : null },
        ali_data: { alias: aliasAli ? aliasAli : null },
      });
      onTagsCatalogChange?.();
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Ошибка при сохранении товара';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  const handleReset = () => {
    setArticle(data.article);
    setName(data.name);
    setBrand(data.brand);
    setCategory(data.category);
    setShowForDistribution(
      data.product_remnants?.show_for_distribution || false
    );
    setIsCombo(data.is_combo);
    setComboComposition(
      data.comboComposition?.map((el) => ({
        ...el,
        value: el.value.toString(),
      })) || []
    );
    setAliasWb(data.wb_data?.alias || '');
    setAliasOzon(data.ozon_data?.alias || '');
    setAliasYa(data.ya_data?.alias || '');
    setAliasSber(data.sber_data?.alias || '');
    setAliasAli(data.ali_data?.alias || '');
    setBarcode(data.wb_data?.barcode || '');
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Form
      style={{
        border: '1px solid #ccc',
        borderRadius: 16,
        padding: 16,
        margin: '16px 0',
      }}
    >
      <Row>
        <Col md={6} xs={12}>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Label>Артикул</Form.Label>
            <Form.Control
              placeholder="Артикул"
              value={article}
              onChange={(e) => setArticle(e.target.value)}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Label>Название</Form.Label>
            <Form.Control
              placeholder="Название"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Label>Бренд</Form.Label>
            <Form.Select
              value={brand?.name}
              onChange={(e) =>
                setBrand(
                  brands.find((el) => el.name === e.target.value) || null
                )
              }
            >
              <option value={''}>Выбрать</option>
              {brands.map((el) => (
                <option key={el.name} value={el.name}>
                  {el.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Label>Категория</Form.Label>
            <Select
              allowClear
              showSearch
              placeholder="Выбрать"
              style={{ width: '100%' }}
              value={category?.id}
              options={categoryOptions}
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLocaleLowerCase('ru')
                  .includes(input.trim().toLocaleLowerCase('ru'))
              }
              onChange={(id: number | undefined) =>
                setCategory(
                  id == null
                    ? null
                    : productCategories.find((el) => el.id === id) || null
                )
              }
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Label>Теги</Form.Label>
            <Select
              mode="tags"
              allowClear
              showSearch
              placeholder="Выбрать или создать"
              style={{ width: '100%' }}
              value={tags.map((t) => t.name)}
              options={tagOptions}
              tokenSeparators={[',']}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLocaleLowerCase('ru')
                  .includes(input.trim().toLocaleLowerCase('ru'))
              }
              onChange={(values: string[]) => {
                setTags(
                  values
                    .map((v) => v.trim())
                    .filter(Boolean)
                    .map((name) => {
                      const existing = availableTags.find(
                        (a) => a.name.toLowerCase() === name.toLowerCase()
                      );
                      return existing
                        ? { id: existing.id, name: existing.name }
                        : { name };
                    })
                );
              }}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Label style={{ display: 'block' }}>WB barcode</Form.Label>
            <Form.Control
              style={{ width: 170, display: 'inline-block' }}
              placeholder="WB barcode"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Check
              type={'checkbox'}
              checked={showForDistribution}
              onChange={(e) => setShowForDistribution(!showForDistribution)}
              label={`Участвует в распределении остатков FBS`}
            />
          </Form.Group>
          <Form.Group style={{ marginBottom: '8px' }}>
            <Form.Check
              type={'checkbox'}
              checked={isCombo}
              onChange={(e) => setIsCombo(!isCombo)}
              label={`Комбо товар`}
            />
          </Form.Group>
          {isCombo && (
            <>
              {comboComposition.map((el, i) => {
                const setArticle = (value: string) =>
                  setComboComposition(
                    comboComposition.map((el, j) =>
                      j === i ? { ...el, article: value } : el
                    )
                  );
                const setValue = (value: string) =>
                  setComboComposition(
                    comboComposition.map((el, j) =>
                      j === i ? { ...el, value: value } : el
                    )
                  );
                const deleteItem = () => {
                  setComboComposition(
                    comboComposition.filter((el, j) => j !== i)
                  );
                };
                return (
                  <Row key={i} style={{ marginBottom: 8 }}>
                    <Col>
                      <Form.Control
                        placeholder="Артикул набора"
                        value={el.article}
                        onChange={(e) => setArticle(e.target.value)}
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        placeholder="Количество единиц"
                        value={el.value}
                        onChange={(e) => setValue(e.target.value)}
                      />
                    </Col>
                    <Col>
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          deleteItem();
                        }}
                      >
                        удалить
                      </Button>
                    </Col>
                  </Row>
                );
              })}
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setComboComposition([
                    ...comboComposition,
                    { article: '', value: '' },
                  ]);
                }}
              >
                добавить артикул в набор
              </Button>
            </>
          )}
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <GoodsMpLinksBlock article={article || data.article} isNew={isNew} />
        </Col>
      </Row>
      <Row>
        <Col md={6} xs={12}>
          <Form.Label
            onClick={() => setShowAlias(!showAlias)}
            style={{ cursor: 'pointer' }}
          >
            Псевдонимы артикула (legacy, только seed){' '}
            {showAlias ? <>&#9650;</> : <>&#9660;</>}
          </Form.Label>
          {showAlias && (
            <Form.Group style={{ marginLeft: '16px' }}>
              <Form.Group style={{ marginBottom: '8px' }}>
                <Form.Label style={{ width: 70, display: 'inline-block' }}>
                  WB
                </Form.Label>
                <Form.Control
                  style={{ width: 170, display: 'inline-block' }}
                  placeholder="WB"
                  value={aliasWb}
                  onChange={(e) => setAliasWb(e.target.value)}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: '8px' }}>
                <Form.Label style={{ width: 70, display: 'inline-block' }}>
                  Ozon
                </Form.Label>
                <Form.Control
                  style={{ width: 170, display: 'inline-block' }}
                  placeholder="ozon"
                  value={aliasOzon}
                  onChange={(e) => setAliasOzon(e.target.value)}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: '8px' }}>
                <Form.Label style={{ width: 70, display: 'inline-block' }}>
                  Ya
                </Form.Label>
                <Form.Control
                  style={{ width: 170, display: 'inline-block' }}
                  placeholder="ya"
                  value={aliasYa}
                  onChange={(e) => setAliasYa(e.target.value)}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: '8px' }}>
                <Form.Label style={{ width: 70, display: 'inline-block' }}>
                  Sber
                </Form.Label>
                <Form.Control
                  style={{ width: 170, display: 'inline-block' }}
                  placeholder="Sber"
                  value={aliasSber}
                  onChange={(e) => setAliasSber(e.target.value)}
                />
              </Form.Group>
              <Form.Group style={{ marginBottom: '8px' }}>
                <Form.Label style={{ width: 70, display: 'inline-block' }}>
                  Ali
                </Form.Label>
                <Form.Control
                  style={{ width: 170, display: 'inline-block' }}
                  placeholder="Ali"
                  value={aliasAli}
                  onChange={(e) => setAliasAli(e.target.value)}
                />
              </Form.Group>
            </Form.Group>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            style={{ margin: '16px 0' }}
            variant="primary"
            type="submit"
            onClick={handleSubmit}
            disabled={!validForm}
          >
            Сохранить
          </Button>
          {!isNew && onCancel && (
            <Button
              style={{ margin: '16px 16px' }}
              variant="secondary"
              type="button"
              onClick={handleReset}
            >
              Cбросить изменения
            </Button>
          )}
          {!isNew && onDelete && (
            <Button
              style={{ margin: '16px 16px 16px 0' }}
              variant="secondary"
              type="button"
              onClick={onDelete}
            >
              Удалить
            </Button>
          )}
          {error && (
            <div
              style={{
                color: 'red',
                marginTop: '8px',
                marginBottom: '16px',
                padding: '8px',
                backgroundColor: '#ffebee',
                borderRadius: '4px',
              }}
            >
              {error}
            </div>
          )}
        </Col>
      </Row>
    </Form>
  );
};
